import React from 'react';
import { useBilling } from 'src/hooks/useBilling';
import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';
import { StripeBanksElementForm } from '@features/onboarding/components/7-finish/components/stripe-payment/StripeBanksElementForm';
import { usePaymentStore } from '@features/onboarding/components/7-finish/store/payment';
import { useInsuranceStore } from 'src/store/insurance';
import { cn, isLollySkin } from '@utils/index';

const getPaymentMethodLabel = (paymentMethod: any) => {
  const bankName = paymentMethod?.usBankAccount?.bankName;
  const last4 = paymentMethod?.usBankAccount?.last4;

  if (paymentMethod?.type === 'us_bank_account' && bankName && last4) {
    return `${bankName} ***********${last4}`;
  }

  return `${paymentMethod.type} - ${paymentMethod.id}`;
};

export const BillingAccounts = () => {
  const { paymentMethods, subscriptionPlans, subscriptions, isLoading, refetch, updateSubscriptionPaymentMethod } = useBilling() as any;
  const { plans: allPlans } = useInsuranceStore();
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [editingSubscriptionId, setEditingSubscriptionId] = React.useState<number | null>(null);
  const { resetPaymentMethodId, setPaymentInitialized } = usePaymentStore();

  // Логирование при монтировании компонента
  React.useEffect(() => {
    console.log('[BillingAccounts] Component mounted', {
      paymentMethodsCount: paymentMethods?.length || 0,
      subscriptionsCount: subscriptions?.length || 0,
      subscriptionPlansCount: subscriptionPlans?.length || 0,
      isLoading,
    });
  }, []);

  // Логирование при изменении состояния модального окна
  React.useEffect(() => {
    console.log('[BillingAccounts] Modal state changed', {
      isEditOpen,
      editingSubscriptionId,
    });
  }, [isEditOpen, editingSubscriptionId]);

  // Callback, который вызывается только после успешной отправки формы Stripe
  const handlePaymentMethodSet = React.useCallback((paymentMethodId: string) => {
    console.log('[BillingAccounts] handlePaymentMethodSet called', {
      paymentMethodId,
      editingSubscriptionId,
      isEditOpen,
    });

    if (!editingSubscriptionId) {
      console.error('[BillingAccounts] No subscription ID set, cannot update payment method');
      return;
    }

    console.log('[BillingAccounts] Payment method set, updating subscription', {
      subscriptionId: editingSubscriptionId,
      paymentMethodId: paymentMethodId,
    });

    setIsEditOpen(false);
    console.log('[BillingAccounts] Modal closed, starting payment method update');

    // Обновим метод оплаты для подписки
    updateSubscriptionPaymentMethod({
      subscriptionId: editingSubscriptionId,
      paymentMethodId: paymentMethodId,
    })
      .then(() => {
        console.log('[BillingAccounts] Payment method updated successfully', {
          subscriptionId: editingSubscriptionId,
          paymentMethodId: paymentMethodId,
        });
        // Перезагрузим данные после успешного обновления
        if (typeof refetch === 'function') {
          console.log('[BillingAccounts] Refetching billing data');
          refetch();
        }
      })
      .catch((error: unknown) => {
        console.error('[BillingAccounts] Failed to update payment method:', {
          error,
          subscriptionId: editingSubscriptionId,
          paymentMethodId: paymentMethodId,
        });
        // Перезагрузим данные даже при ошибке
        if (typeof refetch === 'function') {
          console.log('[BillingAccounts] Refetching billing data after error');
          refetch();
        }
      })
      .finally(() => {
        console.log('[BillingAccounts] Cleaning up state after payment method update');
        // Сбросим ID редактируемой подписки
        setEditingSubscriptionId(null);
        resetPaymentMethodId();
        setPaymentInitialized(false);
        console.log('[BillingAccounts] State cleaned up');
      });
  }, [editingSubscriptionId, updateSubscriptionPaymentMethod, refetch, resetPaymentMethodId]);

  if (isLoading) {
    return (
      <section className="flex flex-col rounded-3xl pb-18 bg-profile-section-bg">
        <div className={cn('text-white px-15 max-md:px-10 max-xs:px-5 rounded-t-3xl py-3.5 pb-2 text-[1.5vw] tracking-wide max-md:text-2xl max-md:py-5', isLollySkin() ? 'bg-lolly-profile-bg' : 'bg-profile-header-bg')}>
          <p className="text-3xl 2xl:text-4xl">Billing Accounts</p>
        </div>
        <div className="flex justify-center items-center py-8">
          <div><AnimatedDots /></div>
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col rounded-3xl pb-18 bg-profile-section-bg">
      <div className={cn('text-white px-15 max-md:px-10 max-xs:px-5 rounded-t-3xl py-3.5 pb-2 text-[1.5vw] tracking-wide max-md:text-2xl max-md:py-5', isLollySkin() ? 'bg-lolly-profile-bg' : 'bg-profile-header-bg')}>
        <p className="text-3xl 2xl:text-4xl">Billing Accounts</p>
      </div>
      <div className="flex flex-col px-14 mt-10 bg-profile-section-bg max-md:px-10 max-xs:px-5">
        <p className="text-xl tracking-[0.02em]">
          Manage how you pay for your plans in one place.
        </p>
        
        {/* Show Add Payment Method button if no payment methods exist */}
        {/* {(!paymentMethods || paymentMethods.length === 0) && (
          <div className="flex justify-center mt-11">
            <button
              className={cn('uppercase rounded-full hover:bg-black/45 py-3 px-8 text-white tracking-wider text-lg font-semimold', isLollySkin() ? 'bg-lolly-profile-bg' : 'bg-profile-header-bg')}
              onClick={() => setIsEditOpen(true)}
            >
              Add Payment Method
            </button>
          </div>
        )} */}
        
        {paymentMethods?.map((paymentMethod: any) => {
          const subscriptionByPaymentMethod = subscriptions?.find((subscription: any) => subscription.paymentMethodId === paymentMethod.id);
          const plansBySubscription = subscriptionPlans?.filter((plan: any) => plan.subscriptionId === subscriptionByPaymentMethod?.id);
          if(!plansBySubscription || plansBySubscription.length === 0){
            return null
          }
          return (
            <div
            key={paymentMethod.id}
            className="flex justify-between mt-11 bg-white rounded-2xl py-4.5 px-7 max-l:flex-col"
          >
            <div className="flex flex-col">
              <p className="text-3xl font-bold tracking-wide text-profile-header-bg">
                Billing Account
              </p>
              <span className="mt-5 text-2xl max-sm:text-xl font-bold text-profile-header-bg max-xl:flex max-xl:flex-wrap">
                Payment Method:{' '}
                <span className="text-black">{getPaymentMethodLabel(paymentMethod)}</span>{' '}
              </span>
              <span className="text-2xl max-sm:text-xl font-bold mt-2 text-profile-header-bg">
                {' '}
                is being used to pay for the following:
              </span>

              <ul className="list-disc ml-6.5 text-2xl max-sm:text-xl font-bold mt-2.5">
                {plansBySubscription?.map((plan: any) => (
                  <li className="" key={plan.id}>Plan: {allPlans.find((p: any) => p.id === plan.planId)?.name}</li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col justify-between mt-2 max-l:gap-8">
              <span className="text-xl py-2 px-4 tracking-tight bg-profile-badge-bg rounded-[10px] max-l:w-fit max-l:mt-2">
                Created: {new Date(paymentMethod.createdAt).toLocaleDateString()}
              </span>
              <button
                className={cn('uppercase rounded-full hover:bg-black/45 w-[98%] py-3 text-white tracking-wider text-lg font-semimold', isLollySkin() ? 'bg-lolly-profile-bg' : 'bg-profile-header-bg')}
                onClick={() => {
                  console.log('[BillingAccounts] Edit Payment Method button clicked', {
                    paymentMethodId: paymentMethod.id,
                    subscriptionId: subscriptionByPaymentMethod?.id,
                    subscriptionByPaymentMethod,
                  });
                  // Сохраняем ID подписки, которую нужно обновить
                  if (subscriptionByPaymentMethod?.id) {
                    console.log('[BillingAccounts] Setting editingSubscriptionId:', subscriptionByPaymentMethod.id);
                    setEditingSubscriptionId(subscriptionByPaymentMethod.id);
                  } else {
                    console.warn('[BillingAccounts] No subscription found for payment method:', paymentMethod.id);
                  }
                  console.log('[BillingAccounts] Opening edit modal');
                  setIsEditOpen(true);
                }}
              >
                Edit Payment Method
              </button>
            </div>
          </div>
          )
        })}
      </div>

      {isEditOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => {
            console.log('[BillingAccounts] Modal backdrop clicked, closing modal');
            setIsEditOpen(false);
            setEditingSubscriptionId(null);
            resetPaymentMethodId();
            setPaymentInitialized(false);
            console.log('[BillingAccounts] Modal closed via backdrop click');
          }} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-[720px] mx-4 p-6">
            <button
              className="absolute right-4 top-3 text-2xl leading-none hover:text-black/70"
              aria-label="Close"
              onClick={() => {
                console.log('[BillingAccounts] Close button clicked, closing modal');
                setIsEditOpen(false);
                setEditingSubscriptionId(null);
                resetPaymentMethodId();
                setPaymentInitialized(false);
                console.log('[BillingAccounts] Modal closed via close button');
              }}
            >
              ×
            </button>
            <h3 className="text-xl font-bold mb-4 text-center">
              {(!paymentMethods || paymentMethods.length === 0) ? 'Add Payment Method' : 'Update Payment Method'}
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6">
              Securely connect your bank account to {(!paymentMethods || paymentMethods.length === 0) ? 'set up' : 'update'} your default payment method.
            </p>
            <StripeBanksElementForm 
              setCurrentStep={() => {}} 
              isHealthhub={true}
              onPaymentMethodSet={(paymentMethodId) => {
                console.log('[BillingAccounts] onPaymentMethodSet callback received', {
                  paymentMethodId,
                  editingSubscriptionId,
                });
                handlePaymentMethodSet(paymentMethodId);
              }}
            />
          </div>
        </div>
      )}
    </section>
  );
};
